import React, { useState } from 'react';
import { DatabaseIcon, PlusIcon, RefreshCwIcon, CheckCircleIcon, AlertCircleIcon, UploadIcon } from 'lucide-react';
interface DataSourcesViewProps {
  userPlan: string;
}
export function DataSourcesView({
  userPlan
}: DataSourcesViewProps) {
  const [showAddSource, setShowAddSource] = useState(false);
  const connectors = [{
    id: 'salesforce',
    name: 'Salesforce',
    icon: '☁️',
    category: 'CRM'
  }, {
    id: 'hubspot',
    name: 'HubSpot',
    icon: '🧡',
    category: 'CRM'
  }, {
    id: 'marketo',
    name: 'Marketo',
    icon: '📧',
    category: 'Marketing'
  }, {
    id: 'snowflake',
    name: 'Snowflake',
    icon: '❄️',
    category: 'Data Warehouse'
  }, {
    id: 'bigquery',
    name: 'BigQuery',
    icon: '🔍',
    category: 'Data Warehouse'
  }, {
    id: 'redshift',
    name: 'Redshift',
    icon: '🔴',
    category: 'Data Warehouse'
  }, {
    id: 's3',
    name: 'Amazon S3',
    icon: '📦',
    category: 'Storage'
  }, {
    id: 'gcs',
    name: 'Google Cloud Storage',
    icon: '☁️',
    category: 'Storage'
  }, {
    id: 'sheets',
    name: 'Google Sheets',
    icon: '📊',
    category: 'Files'
  }, {
    id: 'csv',
    name: 'CSV Upload',
    icon: '📄',
    category: 'Files'
  }];
  const connectedSources = [{
    id: 1,
    name: 'Salesforce Production',
    type: 'Salesforce',
    icon: '☁️',
    status: 'syncing',
    lastSync: '2 minutes ago',
    rows: '1.2M',
    owner: 'Sarah Chen',
    health: 'healthy'
  }, {
    id: 2,
    name: 'HubSpot Marketing',
    type: 'HubSpot',
    icon: '🧡',
    status: 'connected',
    lastSync: '1 hour ago',
    rows: '456K',
    owner: 'Mike Rodriguez',
    health: 'healthy'
  }, {
    id: 3,
    name: 'Product Analytics',
    type: 'BigQuery',
    icon: '🔍',
    status: 'error',
    lastSync: '2 days ago',
    rows: '3.4M',
    owner: 'Data Team',
    health: 'error'
  }];
  return <div className="max-w-7xl mx-auto space-y-6" data-id="element-244">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-245">
        <div data-id="element-246">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-247">Data Sources</h2>
          <p className="text-sm text-gray-600 mt-1" data-id="element-248">
            Connect and sync data from your CRM, data warehouse, and files
          </p>
        </div>
        <button onClick={() => setShowAddSource(true)} disabled={userPlan === 'Free'} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-249">
          <PlusIcon className="w-4 h-4" data-id="element-250" />
          Add Source
        </button>
      </div>
      {/* Connected Sources */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-251">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-252">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-253">
            Connected Sources
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-254">
          <table className="w-full" data-id="element-255">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-256">
              <tr data-id="element-257">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-258">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-259">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-260">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-261">
                  Last Sync
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-262">
                  Rows
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-263">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-264">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-265">
              {connectedSources.map(source => <tr key={source.id} className="hover:bg-gray-50" data-id="element-266">
                  <td className="px-6 py-4" data-id="element-267">
                    <div className="flex items-center gap-3" data-id="element-268">
                      <span className="text-2xl" data-id="element-269">{source.icon}</span>
                      <span className="font-medium text-gray-900" data-id="element-270">
                        {source.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-271">
                    {source.type}
                  </td>
                  <td className="px-6 py-4" data-id="element-272">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${source.status === 'syncing' ? 'bg-blue-100 text-blue-700' : source.status === 'connected' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-id="element-273">
                      {source.status === 'error' ? <AlertCircleIcon className="w-3 h-3" data-id="element-274" /> : <CheckCircleIcon className="w-3 h-3" data-id="element-275" />}
                      {source.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-276">
                    {source.lastSync}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-277">
                    {source.rows}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-278">
                    {source.owner}
                  </td>
                  <td className="px-6 py-4" data-id="element-279">
                    <button disabled={userPlan === 'Free'} className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-280">
                      <RefreshCwIcon className="w-4 h-4 text-gray-600" data-id="element-281" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
      {/* Available Connectors */}
      {showAddSource && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-282">
          <div className="flex items-center justify-between mb-4" data-id="element-283">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-284">
              Available Connectors
            </h3>
            <button onClick={() => setShowAddSource(false)} className="text-sm text-gray-600 hover:text-gray-900" data-id="element-285">
              Cancel
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-286">
            {connectors.map(connector => <button key={connector.id} disabled={userPlan === 'Free'} className="p-4 border-2 border-gray-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all text-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-287">
                <span className="text-3xl block mb-2" data-id="element-288">{connector.icon}</span>
                <p className="text-sm font-medium text-gray-900" data-id="element-289">
                  {connector.name}
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-290">
                  {connector.category}
                </p>
              </button>)}
          </div>
        </div>}
      {/* Upload CSV */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-291">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-292">
          Upload File
        </h3>
        <div className={`border-2 border-dashed border-gray-300 rounded-lg p-8 text-center ${userPlan === 'Free' ? 'opacity-50 cursor-not-allowed' : 'hover:border-red-500 cursor-pointer'}`} data-id="element-293">
          <UploadIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-294" />
          <p className="text-sm text-gray-900 font-medium mb-1" data-id="element-295">
            Drop CSV, Parquet, or JSON files here
          </p>
          <p className="text-xs text-gray-600" data-id="element-296">Max file size: 100 MB</p>
        </div>
      </div>
    </div>;
}